import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/Layout";
import { getArticleBySlug, newsArticles, type ArticleBlock } from "@/lib/news-data";

export const Route = createFileRoute("/news/$slug")({
  loader: ({ params }) => {
    const article = getArticleBySlug(params.slug);
    if (!article) throw notFound();
    return { article };
  },
  head: ({ params, loaderData }) => {
    const article = loaderData?.article;
    if (!article) {
      return { meta: [{ title: "Article not found — ExpatCare PH" }] };
    }
    const url = `https://expatcareph.com/news/${params.slug}`;
    const metaTitle = article.seoTitle ?? article.title;
    const metaDescription = article.seoDescription ?? article.excerpt;
    return {
      meta: [
        { title: metaTitle },
        { name: "description", content: metaDescription },
        { property: "og:title", content: metaTitle },
        { property: "og:description", content: metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: new URL(article.image, "https://expatcareph.com").href },
        { name: "twitter:image", content: new URL(article.image, "https://expatcareph.com").href },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.image,
            articleSection: article.category,
            mainEntityOfPage: url,
            datePublished: article.datePublished,
            author: {
              "@type": "Organization",
              name: "ExpatCare Philippines",
              url: "https://expatcareph.com",
            },
            publisher: {
              "@type": "Organization",
              name: "ExpatCare Philippines",
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <SiteLayout>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center lg:px-8">
        <h1 className="text-3xl font-bold">Article not found</h1>
        <p className="mt-3 text-muted-foreground">The article you're looking for doesn't exist.</p>
        <Link to="/news" className="mt-6 inline-block text-sm font-semibold text-accent hover:underline">
          ← Back to News
        </Link>
      </div>
    </SiteLayout>
  ),
  errorComponent: ({ error }) => (
    <SiteLayout>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center lg:px-8">
        <h1 className="text-3xl font-bold">Something went wrong</h1>
        <p className="mt-3 text-muted-foreground">{error instanceof Error ? error.message : "Unexpected error"}</p>
      </div>
    </SiteLayout>
  ),
  component: ArticlePage,
});

function renderBlock(block: ArticleBlock, idx: number) {
  switch (block.type) {
    case "h2":
      return <h2 key={idx} className="mt-8 text-2xl font-bold text-foreground">{block.text}</h2>;
    case "h3":
      return <h3 key={idx} className="mt-6 text-xl font-semibold text-foreground">{block.text}</h3>;
    case "p":
      return <p key={idx} className="mt-4 text-base leading-relaxed text-foreground/90">{block.text}</p>;
    case "ul":
      return (
        <ul key={idx} className="mt-4 list-disc space-y-2 pl-6 text-foreground/90">
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      );
    case "table":
      return (
        <div key={idx} className="mt-6 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-muted">
                {block.headers.map((h, i) => (
                  <th key={i} className="border border-border px-4 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className="border border-border px-4 py-2">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

function ArticlePage() {
  const { article } = Route.useLoaderData();
  const related = newsArticles.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <SiteLayout>
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-wide opacity-90">{article.category}</p>
          <h1 className="mt-3 text-3xl font-bold leading-tight md:text-4xl">{article.title}</h1>
          <p className="mt-4 text-sm opacity-90">
            By ExpatCare Philippines ·{" "}
            <time dateTime={article.datePublished}>
              {new Date(`${article.datePublished}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}
            </time>
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-12 lg:px-8">
        <img
          src={article.image}
          alt={article.title}
          className="mb-8 h-auto w-full rounded-lg object-cover shadow-sm"
        />
        <div className="prose-none">
          {article.content.map((b: any, i: number) => renderBlock(b, i))}
        </div>

        <div className="mt-12 rounded-lg bg-muted p-6 text-center">
          <h2 className="text-xl font-semibold text-foreground">Have questions?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Get in touch with our team to learn more about assisted living in the Philippines.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90">
              Contact Us
            </Link>
            <Link to="/book" className="rounded-md border border-primary px-5 py-2 text-sm font-semibold text-primary hover:bg-primary hover:text-primary-foreground">
              Book Appointment
            </Link>
          </div>
        </div>
      </article>

      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground">More articles</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/news/$slug" params={{ slug: r.slug }} className="group overflow-hidden rounded-lg border border-border bg-card shadow-sm transition hover:shadow-md">
                <img src={r.image} alt={r.title} className="h-40 w-full object-cover" loading="lazy" />
                <div className="p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-accent">{r.category}</p>
                  <h3 className="mt-2 text-base font-semibold text-foreground group-hover:text-accent">{r.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
